import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const Contact = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    axios.get(`${API}/public/site-sections/contact_info`)
      .then(res => setContactInfo(res.data?.content))
      .catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <Mail className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Get in Touch</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-6xl font-bold text-white mb-4" data-testid="contact-title">
            Contact Us
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            <div className="glass-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-electric-cyan/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-electric-cyan" />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-white mb-1">Email</h3>
                  <p className="text-slate-400">{contactInfo?.email || 'contact@physicsco.sg'}</p>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-electric-cyan/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-electric-cyan" />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-white mb-1">Phone</h3>
                  <p className="text-slate-400">{contactInfo?.phone || '+65 8888 8888'}</p>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-electric-cyan/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-electric-cyan" />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-white mb-1">Location</h3>
                  <p className="text-slate-400">{contactInfo?.address || 'Singapore'}</p>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-electric-cyan/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-electric-cyan" />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-white mb-1">Hours</h3>
                  <p className="text-slate-400">{contactInfo?.hours || 'Mon-Sat: 9AM - 9PM'}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <form onSubmit={handleSubmit} className="glass-card p-8">
              <h2 className="font-outfit text-2xl font-bold text-white mb-6">Send us a Message</h2>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Name *</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="input-dark"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="input-dark"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Phone</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+65 XXXX XXXX"
                    className="input-dark"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Subject *</label>
                  <Input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="How can we help?"
                    className="input-dark"
                    data-testid="contact-subject"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm text-slate-400 mb-2">Message *</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us more about your inquiry..."
                  rows={5}
                  className="input-dark resize-none"
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full md:w-auto flex items-center justify-center gap-2"
                data-testid="contact-submit"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-deep-blue"></div>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
